<script setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import QuickActionButtonsAPI from 'dashboard/api/quickActionButtons';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const { currentAccount } = useAccount();

const loadingButtons = reactive({});

const buttons = computed(() => {
  const all = currentAccount.value?.settings?.quick_action_buttons || [];
  return all.filter(b => b.enabled !== false);
});

const COLOR_CLASSES = {
  blue: 'bg-woot-500 hover:bg-woot-600',
  green: 'bg-green-500 hover:bg-green-600',
  red: 'bg-red-500 hover:bg-red-600',
  yellow: 'bg-yellow-300 hover:bg-yellow-400 text-gray-900',
  violet: 'bg-violet-500 hover:bg-violet-600',
  orange: 'bg-orange-500 hover:bg-orange-600',
  pink: 'bg-pink-500 hover:bg-pink-600',
  teal: 'bg-teal-500 hover:bg-teal-600',
  cyan: 'bg-cyan-500 hover:bg-cyan-600',
  indigo: 'bg-indigo-500 hover:bg-indigo-600',
  rose: 'bg-rose-500 hover:bg-rose-600',
  emerald: 'bg-emerald-500 hover:bg-emerald-600',
  amber: 'bg-amber-400 hover:bg-amber-500 text-gray-900',
  slate: 'bg-n-slate-9 hover:bg-n-slate-10',
};

const getButtonClasses = color => {
  return COLOR_CLASSES[color] || COLOR_CLASSES.blue;
};

const executeAction = async button => {
  loadingButtons[button.id] = true;
  try {
    await QuickActionButtonsAPI.execute({
      conversationId: props.conversationId,
      buttonId: button.id,
    });
    useAlert(t('GENERAL_SETTINGS.FORM.QUICK_ACTION_BUTTONS.EXECUTE_SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.FORM.QUICK_ACTION_BUTTONS.EXECUTE_ERROR'));
  } finally {
    loadingButtons[button.id] = false;
  }
};
</script>

<template>
  <div class="px-2 py-3">
    <div v-if="!buttons.length" class="text-sm text-n-slate-11 p-2">
      {{ $t('GENERAL_SETTINGS.FORM.QUICK_ACTION_BUTTONS.EMPTY') }}
    </div>
    <div v-else class="flex flex-wrap gap-2">
      <button
        v-for="button in buttons"
        :key="button.id"
        class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium text-white transition-colors duration-150 disabled:opacity-50"
        :class="getButtonClasses(button.color)"
        :disabled="loadingButtons[button.id]"
        @click="executeAction(button)"
      >
        <svg
          v-if="loadingButtons[button.id]"
          class="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
        <fluent-icon
          v-else-if="button.icon"
          :icon="button.icon"
          size="14"
          class="-ml-0.5 mr-1.5"
        />
        {{ button.label || button.name }}
      </button>
    </div>
  </div>
</template>
